"use client";
import { useState } from 'react'
import Link from 'next/link';


import { Trash2, CircleCheck as CheckCircle, ClipboardList, Package, Globe } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useQuoteStore } from '@/stores/quoteStore'
import { submitQuoteRequest } from '@/lib/api'
import { siteConfig } from '@/data/siteConfig'

const schema = z.object({
  customer_name: z.string().min(2, 'Name is required'),
  email: z.string().email('Valid email required'),
  company: z.string().optional(),
  phone: z.string().optional(),
  country: z.string().optional(),
  incoterm: z.string().optional(),
  currency: z.string().optional(),
  notes: z.string().optional(),
})
type FormData = z.infer<typeof schema>

export function QuotePage() {
  const { items, removeItem, clearQuote } = useQuoteStore()
  const [submitted, setSubmitted] = useState(false)
  const [referenceId, setReferenceId] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [incoterm, setIncoterm] = useState('FOB')
  const [currency, setCurrency] = useState('USD')

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  async function onSubmit(data: FormData) {
    if (items.length === 0) {
      toast.error('Your quote basket is empty.')
      return
    }
    setSubmitting(true)
    try {
      const result = await submitQuoteRequest({
        customer_name: data.customer_name,
        email: data.email,
        company: data.company ?? '',
        phone: data.phone ?? '',
        country: data.country ?? '',
        incoterm,
        currency,
        line_items: items,
        notes: data.notes ?? '',
      })
      setReferenceId(result.id.slice(0, 8).toUpperCase())
      clearQuote()
      setSubmitted(true)
    } catch {
      toast.error('Failed to submit quote. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 bg-accent">
          <CheckCircle className="h-8 w-8 text-brand-accent" />
        </div>
        <h1 className="text-2xl font-bold mb-3">Quote Request Received</h1>
        <p className="text-muted-foreground mb-2">
          Reference: <strong className="font-mono text-foreground">JM-{referenceId}</strong>
        </p>
        <p className="text-muted-foreground mb-8 leading-relaxed">
          Our export desk will respond within 1 business day with MOQ, tiered pricing, lead time, and available Incoterms for your destination.
        </p>
        <div className="flex gap-3 justify-center">
          <Link href="/"><Button className="bg-primary text-primary-foreground hover:bg-primary/90">Back to Home</Button></Link>
          <Link href="/tower-bolts"><Button variant="outline">Browse More</Button></Link>
        </div>
      </div>
    )
  }

  return (
    <>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <p className="text-xs text-brand-accent font-semibold tracking-[0.2em] uppercase mb-3">Export Desk</p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-3">Request a Bulk Quote</h1>
          <p className="text-muted-foreground max-w-2xl leading-relaxed">
            Select items, tell us your destination and preferred Incoterm, and our export team will respond within 1 business day with pricing, MOQ, and lead-time.
          </p>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-border rounded-2xl bg-muted/30">
            <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-lg mb-2">Your quote basket is empty</p>
            <p className="text-sm text-muted-foreground mb-6">Browse our range and add items to build your export enquiry.</p>
            <div className="flex gap-3 justify-center">
              <Link href="/tower-bolts"><Button className="bg-primary text-primary-foreground hover:bg-primary/90">Tower Bolts</Button></Link>
              <Link href="/cabinet-handles"><Button variant="outline">Cabinet Handles</Button></Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 flex flex-col gap-6">
                <Card>
                  <CardHeader className="pb-3">
                    <h2 className="sr-only">Selected Items</h2>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Package className="h-4 w-4 text-brand-accent" />
                      Selected Items ({items.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col divide-y divide-border">
                      {items.map((item) => (
                        <div key={item.variantId} className="py-4 flex items-center gap-4">
                          <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0 border border-border/60">
                            <img
                              src={item.imageUrl || `https://placehold.co/80x80/f5f0e8/2d2d2d?text=${encodeURIComponent(item.sku)}`}
                              alt={item.productName}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = `https://placehold.co/80x80/f5f0e8/2d2d2d?text=${encodeURIComponent(item.sku)}`
                              }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{item.productName}</p>
                            <p className="text-xs text-muted-foreground font-mono mt-0.5">{item.sku}</p>
                            <div className="flex gap-1.5 mt-1.5 flex-wrap">
                              <Badge variant="secondary" className="text-[10px]">{item.size}</Badge>
                              <Badge variant="secondary" className="text-[10px]">{item.material}</Badge>
                              <Badge variant="secondary" className="text-[10px]">{item.finish}</Badge>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeItem(item.variantId)}
                            className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0 p-2"
                            aria-label="Remove item"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <div className="rounded-xl bg-accent/50 border border-accent-foreground/10 p-5 flex gap-4">
                  <Globe className="h-5 w-5 text-brand-accent flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-foreground/80 leading-relaxed">
                    <strong className="text-foreground">All enquiries are treated as bulk / container-load orders.</strong>{' '}
                    Tell us the SKUs and destination; we will confirm MOQ per SKU, consolidated container loading options, and tiered pricing in your quotation.
                  </div>
                </div>
              </div>

              <div>
                <Card className="sticky top-24">
                  <CardHeader className="pb-3">
                    <h2 className="sr-only">Your Details</h2>
                    <CardTitle className="text-base">Your Details</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-4">
                    <div>
                      <Label htmlFor="q-name">Name *</Label>
                      <Input id="q-name" {...register('customer_name')} className="mt-1.5" placeholder="Full name" />
                      {errors.customer_name && <p className="text-xs text-destructive mt-1">{errors.customer_name.message}</p>}
                    </div>
                    <div>
                      <Label htmlFor="q-email">Email *</Label>
                      <Input id="q-email" type="email" {...register('email')} className="mt-1.5" placeholder="you@company.com" />
                      {errors.email && <p className="text-xs text-destructive mt-1">{errors.email.message}</p>}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="q-company">Company</Label>
                        <Input id="q-company" {...register('company')} className="mt-1.5" placeholder="Optional" />
                      </div>
                      <div>
                        <Label htmlFor="q-phone">Phone</Label>
                        <Input id="q-phone" {...register('phone')} className="mt-1.5" placeholder="+country..." />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="q-country">Destination Country</Label>
                      <Input id="q-country" {...register('country')} className="mt-1.5" placeholder="e.g. United Arab Emirates" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Incoterm</Label>
                        <Select value={incoterm} onValueChange={setIncoterm}>
                          <SelectTrigger className="mt-1.5 w-full"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {siteConfig.export.incoterms.map((t) => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Currency</Label>
                        <Select value={currency} onValueChange={setCurrency}>
                          <SelectTrigger className="mt-1.5 w-full"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {siteConfig.export.currencies.map((c) => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="q-notes">Notes</Label>
                      <Textarea id="q-notes" {...register('notes')} rows={3} className="mt-1.5" placeholder="Target quantity, timeline, packaging, private-label needs..." />
                    </div>
                    <Separator />
                    <Button type="submit" disabled={submitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11">
                      {submitting ? 'Submitting...' : 'Submit Export Inquiry'}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center leading-relaxed">
                      Response within 1 business day. L/C, T/T, and D/P payment terms supported.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </form>
        )}
      </div>
    </>
  )
}
